import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MarkunreadIcon from '@mui/icons-material/Markunread';
import SendIcon from '@mui/icons-material/Send';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid/locales';
import { LicenseInfo } from '@mui/x-license';
import { get } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateForm from '../../../components/ui-component/form-component/create-form/CreateForm';
import CreateNachricht from '../../../components/ui-component/nachrichten/CreateNachricht';
import { useFetchApplicationsForNachrichten } from '../../../hooks/FetchApplications';
import { useFetchNachrichten } from '../../../hooks/FetchNachrichten';
import { MUIX_LICENSE } from '../../../lib/constants/constant';
import { PAGES_URLS } from '../../../lib/constants/routes';
import { DisplayDateTime } from '../../../utils/formats';
import { T, getFieldNamefromDataPath, getLabelFromDataPath } from '../../../utils/helper';

// import schemas
import Schema from '../../../lib/paths/portal45nachricht_TABLE_Paths.json';
let P = Schema.attributes;

LicenseInfo.setLicenseKey(MUIX_LICENSE);

function MeineNachrichten() {
	const navigate = useNavigate();

	const [ordnerValue, setOrdnerValue] = useState({ ordner: 'Posteingang' });
	const [showCreateNachricht, setShowCreatenachricht] = useState<boolean>(false);
	const { loading, data, error } = useFetchNachrichten(showCreateNachricht, get(ordnerValue, 'ordner', 'Posteingang'));
	const { data: applications } = useFetchApplicationsForNachrichten();
	const showNeueNachricht = (applications?.length ?? 0) > 0;
	const columns = useMemo(() => {
		let column: any[] = [
			// {
			// 	field: 'id',
			// 	headerName: 'ID',
			// 	maxWidth: 50,
			// },
			{
				field: getFieldNamefromDataPath(P.Foerderantrag.schema.attributes.Allgemein_Projekttitel.path),
				headerName: getLabelFromDataPath(P.Foerderantrag.schema.attributes.Allgemein_Projekttitel.path),
				width: 200,
			},
			{
				field: getFieldNamefromDataPath(P.Foerderantrag.schema.attributes.Allgemein_Eingangsnummer.path),
				headerName: getLabelFromDataPath(P.Foerderantrag.schema.attributes.Allgemein_Eingangsnummer.path),
				width: 140,
			},
			{
				field: getFieldNamefromDataPath(P.Allgemein_Betreff.path),
				headerName: getLabelFromDataPath(P.Allgemein_Betreff.path),
				flex: 1,
			},
			{
				field: 'Erstellt_am',
				headerName: 'Erstellt am',
				flex: 1,
				maxWidth: 170,
			},
		];

		if (get(ordnerValue, 'ordner') === 'Posteingang') {
			column.push({
				field: 'read',
				headerName: 'Gelesen',
				sortable: false,
				width: 90,
				disableColumnMenu: true,
				renderCell: (params: any) => {
					return params.row.Allgemein_gelesen ? <DraftsOutlinedIcon fontSize='small' /> : <MarkunreadIcon fontSize='small' />;
				},
			});
		}

		return column;
	}, [ordnerValue]);

	const rows = useMemo(() => {
		return (
			data?.map((ele) => ({
				id: get(ele, 'id'),
				[getFieldNamefromDataPath(P.Allgemein_Betreff.path)]: T(get(ele, P.Allgemein_Betreff.path)),

				[getFieldNamefromDataPath(P.Foerderantrag.schema.attributes.Allgemein_Projekttitel.path)]: T(
					get(ele, P.Foerderantrag.schema.attributes.Allgemein_Projekttitel.path),
				),

				[getFieldNamefromDataPath(P.Foerderantrag.schema.attributes.Allgemein_Eingangsnummer.path)]: T(
					get(ele, P.Foerderantrag.schema.attributes.Allgemein_Eingangsnummer.path),
				),
				Erstellt_am: DisplayDateTime(get(ele, 'attributes.createdAt')),
				[getFieldNamefromDataPath(P.Allgemein_Ordner.path)]: get(ele, P.Allgemein_Ordner.path),
				[getFieldNamefromDataPath(P.Allgemein_gelesen.path)]: get(ele, P.Allgemein_gelesen.path),
			})) ?? []
		);
	}, [data]);

	if (error) console.error(' error', error);
	const formSTRUC = [
		{
			fields: [
				{
					label: 'Ordner',
					path: 'ordner',
					type: 'singleSelect',
					options: [
						{ label: 'Posteingang', value: 'Posteingang' },
						{ label: 'Postausgang', value: 'Postausgang' },
					],
					sx: { width: '300px' },
				},
			],
		},
	];
	return (
		<>
			<CreateNachricht
				showNachrichtDialog={showCreateNachricht}
				handleCloseDialog={() => setShowCreatenachricht(false)}
				applications={applications as any[]}
			/>
			<Container>
				<Stack direction={'row'} justifyContent={'space-between'}>
					<Typography variant='h3' component='h2'>
						Meine Nachrichten
					</Typography>
					{showNeueNachricht && (
						<Button onClick={() => setShowCreatenachricht(true)} endIcon={<SendIcon />}>
							{' '}
							Neue Nachricht
						</Button>
					)}
				</Stack>{' '}
				<CreateForm
					formStruc={formSTRUC}
					formValues={ordnerValue}
					updateFormValuesFunc={(_path: string, value: any) => setOrdnerValue({ ordner: value })}
				/>
				<Box sx={{ mt: '2rem' }}>
					<DataGridPremium
						autoHeight
						loading={loading}
						rows={rows}
						columns={columns}
						onRowClick={(param) => navigate(`${PAGES_URLS.NachrichtenDetails}`, { state: { id: param.id } })}
						pinnedColumns={{ right: ['read'] }}
						getRowClassName={(params) =>
							`${params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'} message-read-status--${
								params.row.Allgemein_gelesen
							}--message-folder-${params.row.Allgemein_Ordner}`
						}
						disableColumnMenu
						pagination
						pageSizeOptions={[5, 10, 25, 50, 100]}
						initialState={{
							pagination: {
								paginationModel: { pageSize: 10, page: 0 },
							},
						}}
						localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
					/>
				</Box>
			</Container>
		</>
	);
}

export default MeineNachrichten;
